"use client";

import { useCallback, useState } from "react";
import Image from "next/image";
import { Upload, X, Camera } from "lucide-react";
import { cn } from "@/lib/utils";

interface VehicleUploadProps {
  imageUrl: string | null;
  onImageChange: (url: string | null) => void;
  onFileSelect: (file: File) => void;
  isUploading?: boolean;
  compact?: boolean;
  disabled?: boolean;
}

export function VehicleUpload({
  imageUrl,
  onImageChange,
  onFileSelect,
  isUploading,
  compact = false,
  disabled = false,
}: VehicleUploadProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) {
        onFileSelect(file);
      }
    },
    [onFileSelect, disabled]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && !disabled) {
        onFileSelect(file);
      }
    },
    [onFileSelect, disabled]
  );

  const handleClear = useCallback(() => {
    if (disabled) return;
    onImageChange(null);
  }, [onImageChange, disabled]);

  if (imageUrl) {
    return (
      <div className={cn(
        "relative w-full bg-muted rounded-xl overflow-hidden",
        compact ? "aspect-4/3" : "aspect-4/3 sm:aspect-video",
        disabled && "opacity-80 grayscale-20"
      )}>
        <Image
          src={imageUrl}
          alt="Car"
          fill
          className="w-full h-full object-contain"
          sizes={compact ? "(max-width: 640px) 100vw, 50vw" : "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"}
        />
        {!disabled && (
          <button
            onClick={handleClear}
            className={cn(
              "absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-full hover:bg-black/80 transition-colors",
              compact ? "p-1.5" : "p-2"
            )}
            aria-label="Remove image"
          >
            <X className={cn(compact ? "w-4 h-4" : "w-5 h-5", "text-white")} />
          </button>
        )}
        {isUploading && (
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className={cn(
                "animate-spin rounded-full border-2 border-primary border-t-transparent",
                compact ? "h-6 w-6" : "h-10 w-10"
              )} />
              <span className="text-sm text-muted-foreground">Uploading...</span>
            </div>
          </div>
        )}                
      </div>
    );
  }

  return (
    <label
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "group flex items-center justify-center w-full",
        "border-2 border-dashed rounded-xl",
        "backdrop-blur-sm",
        disabled ? "cursor-not-allowed opacity-60 bg-muted/20" : "cursor-pointer",
        "transition-all duration-300",
        compact ? "py-6" : "py-8",
        !disabled && isDragging
          ? "border-primary bg-primary/10 scale-[0.99]"
          : !disabled 
            ? "border-muted-foreground/25 bg-linear-to-br from-primary/5 via-transparent to-violet-500/5 hover:border-primary/50 hover:from-primary/10 hover:to-violet-500/10" 
            : "border-muted-foreground/20"
      )}
    >
      <div className="flex items-center gap-4 px-4">
        {/* Icon */}
        <div className={cn(
          "relative rounded-full shrink-0 transition-transform duration-300",
          isUploading ? "bg-primary/10" : disabled ? "bg-muted" : "bg-primary/10 group-hover:scale-110",
          "p-3"
        )}>
          {isUploading ? (
            <div className="animate-spin rounded-full border-2 border-primary border-t-transparent h-6 w-6" />
          ) : (
            <>
              <Upload className={cn(
                disabled ? "text-muted-foreground/50" : "text-primary",
                "w-6 h-6"
              )} />
              {/* Camera badge for mobile */}
              {!disabled && (
                <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1 sm:hidden">
                  <Camera className="w-2.5 h-2.5 text-primary-foreground" />
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Text - horizontal layout */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground text-sm sm:text-base leading-tight">
            {isUploading ? "Uploading..." : disabled ? "Upload Disabled" : "Tap to upload a photo"}
          </p>
          
          {!disabled && !isUploading && (
            <p className="text-xs text-primary/70 mt-0.5 leading-snug">
              Side view with visible wheels works best
            </p>
          )}
        </div>
      </div>
      
      {/* File input with capture for mobile camera */}
      <input
        type="file"
        className="hidden"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileInput}
        disabled={isUploading || disabled}
      />
    </label>
  );
}
